import type { LoadStatus, TeamMemberWorkload, UnifiedWorkItem } from '../../../modules/minha-mesa/types/workItem';
import { getOccupancyTier } from '../executive/opsExecutiveShared';

/** Limite de tarefas ativas ao mesmo tempo por operador. Não é tarefas/h. */
export const MAX_ACTIVE_TASKS_PER_OPERATOR = 8;
/** Alias do limite simultâneo. Não representa produtividade em tarefas/pessoa/h. */
export const CAPACITY_PER_OPERATOR = MAX_ACTIVE_TASKS_PER_OPERATOR;

export type TeamSortColumn =
  | 'name'
  | 'status'
  | 'in_progress'
  | 'pending'
  | 'overdue'
  | 'completed'
  | 'occupation'
  | 'sla'
  | 'bottleneck';

export type SortDirection = 'asc' | 'desc';
export type KpiFilter = null | 'overdue' | 'critical';

export const STATUS_LABELS: Record<LoadStatus, string> = {
  normal: 'Normal',
  high: 'Atenção',
  overloaded: 'Sobrecarga',
};

export const STATUS_STYLES: Record<LoadStatus, { bg: string; text: string; ring: string }> = {
  normal: { bg: 'bg-emerald-100 dark:bg-emerald-500/15', text: 'text-emerald-700 dark:text-emerald-300', ring: '' },
  high: { bg: 'bg-amber-100 dark:bg-amber-500/15', text: 'text-amber-700 dark:text-amber-300', ring: '' },
  overloaded: { bg: 'bg-red-100 dark:bg-red-500/15', text: 'text-red-700 dark:text-red-300', ring: 'ring-1 ring-red-300 dark:ring-red-500/30' },
};

export function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return (parts[0]?.[0] ?? '?').toUpperCase();
}

/** Risco da equipe: carga alta/sobrecarga, atraso ou SLA. Não é o status de carga. */
export function isCritical(m: TeamMemberWorkload): boolean {
  const occupationPct = Math.round((m.total_active / MAX_ACTIVE_TASKS_PER_OPERATOR) * 100);
  const tier = getOccupancyTier(occupationPct);
  return tier === 'critical' || tier === 'high' || m.tasks_overdue > 0 || m.sla_violated_pct > 0;
}

/** Pressão só da ocupação simultânea. Atraso não eleva este nível. */
export function visibleBottleneck(member: {
  tasks_overdue: number;
  total_active: number;
}): { text: string; color: string } {
  const occupationPct = Math.round((member.total_active / MAX_ACTIVE_TASKS_PER_OPERATOR) * 100);
  const tier = getOccupancyTier(occupationPct);
  if (tier === 'critical') return { text: 'Alto', color: 'text-red-600 dark:text-red-400' };
  if (tier === 'high') return { text: 'Médio', color: 'text-amber-600 dark:text-amber-400' };
  if (member.total_active > 0) return { text: 'Baixo', color: 'text-emerald-600 dark:text-emerald-400' };
  return { text: '--', color: 'text-[var(--text-muted)]' };
}

export function bottleneckLabel(score: number): { text: string; color: string } {
  if (score > 10) return { text: 'Alto', color: 'text-red-600 dark:text-red-400' };
  if (score > 5) return { text: 'Médio', color: 'text-amber-600 dark:text-amber-400' };
  if (score > 0) return { text: 'Baixo', color: 'text-emerald-600 dark:text-emerald-400' };
  return { text: '--', color: 'text-[var(--text-muted)]' };
}

function memberSortValue(m: TeamMemberWorkload, col: TeamSortColumn): number | string {
  switch (col) {
    case 'name': return m.user_name.toLowerCase();
    case 'status': {
      const pct = Math.round((m.total_active / MAX_ACTIVE_TASKS_PER_OPERATOR) * 100);
      const rank = { low: 0, medium: 1, high: 2, critical: 3 } as const;
      return rank[getOccupancyTier(pct)];
    }
    case 'in_progress': return m.tasks_in_progress;
    case 'pending': return m.tasks_pending;
    case 'overdue': return m.tasks_overdue;
    case 'completed': return m.tasks_completed_today;
    case 'occupation': return m.total_active;
    case 'sla': return m.sla_violated_pct;
    case 'bottleneck': return m.bottleneck_score;
  }
}

export function sortMembersByColumn(
  members: TeamMemberWorkload[],
  col: TeamSortColumn,
  direction: SortDirection,
): TeamMemberWorkload[] {
  const mult = direction === 'asc' ? 1 : -1;
  return [...members].sort((a, b) => {
    const va = memberSortValue(a, col);
    const vb = memberSortValue(b, col);
    if (typeof va === 'string' && typeof vb === 'string') return va.localeCompare(vb) * mult;
    return ((va as number) - (vb as number)) * mult;
  });
}

const STATUS_SORT_ORDER: Record<string, number> = {
  overdue: 0,
  in_progress: 1,
  pending: 2,
  waiting: 3,
};

export function activitySortKey(item: UnifiedWorkItem): number {
  if (item.is_overdue) return 0;
  return STATUS_SORT_ORDER[item.status] ?? 4;
}

export const SOURCE_BORDER: Record<string, string> = {
  problem: 'border-l-red-500',
  workflow: 'border-l-violet-500',
  action: 'border-l-blue-500',
  monitoring: 'border-l-cyan-500',
};
